/** Player-facing copy for tactical patterns: plain-language meaning, next-game habit, and evidence summaries. */
export const motifBeginnerExplanation = (motif: string): string => {
  switch (motif) {
    case "blunder":
      return "A move that seriously worsened your position according to verified analysis.";
    case "mistake":
      return "A meaningful error that gave away part of your position, but was less severe than a blunder.";
    case "imprecision":
      return "A small inaccuracy where a stronger move was available.";
    case "time_pressure":
    case "low_time":
      return "Costly decisions that happened while your clock was running low.";
    case "missed_check":
      return "A verified position where a stronger legal check was available.";
    case "missed_capture":
      return "A verified position where a stronger legal capture was available.";
    case "hanging_piece":
      return "A piece could be won because it was left undefended or could not be saved.";
    case "fork":
      return "One piece could attack two valuable targets at the same time.";
    case "pin":
      return "A piece could not safely move because doing so would expose a more valuable piece or the king.";
    case "skewer":
      return "A valuable front piece is attacked and, after it moves, a piece behind it can be won.";
    case "discovered_attack":
      return "Moving one piece reveals an attack from another piece behind it.";
    case "back_rank_threat":
      return "The king is vulnerable on its back rank because it has too few escape squares.";
    case "deflection":
      return "A defender is forced away from the piece or square it must protect.";
    case "sacrifice":
      return "Material is deliberately given up for a concrete stronger payoff.";
    case "zwischenzug":
      return "An in-between forcing move is played before the expected recapture or reply.";
    case "mating_net":
      return "A sequence of forcing moves traps the king and leads to checkmate.";
    case "overloaded_defender":
      return "One defending piece has too many important jobs and cannot perform all of them.";
    default:
      return "A recurring decision pattern found in your analyzed games.";
  }
};

export const nextBoardHabit = (motif: string): string => {
  switch (motif) {
    case "blunder":
      return "Before committing, check every capture, check, and threat your opponent has.";
    case "mistake":
      return "When the position changes, compare your move with one calm alternative.";
    case "imprecision":
      return "In quiet positions, improve your worst-placed piece before pushing.";
    case "missed_check":
      return "Before every move, scan every legal check and ask where the king must go.";
    case "missed_capture":
      return "Scan checks, captures, and threats; calculate the safest forcing capture first.";
    case "hanging_piece":
      return "Before releasing the piece, count its attackers and defenders on the destination square.";
    case "time_pressure":
    case "low_time":
      return "Use your opponent's turn to choose your candidate moves before the clock becomes the problem.";
    case "fork":
      return "Before moving, scan every forcing square from which one piece attacks two valuable targets.";
    case "pin":
    case "skewer":
      return "Trace every open rank, file, and diagonal through the target before choosing your move.";
    case "discovered_attack":
      return "When a piece moves, inspect the line it vacates as carefully as its destination.";
    case "back_rank_threat":
    case "mating_net":
      return "Count the king's legal flight squares before calculating checks.";
    case "deflection":
    case "overloaded_defender":
      return "Name each defender and ask what else falls if that defender is forced away.";
    case "zwischenzug":
      return "Before an automatic recapture, look once for a forcing check or stronger capture.";
    case "sacrifice":
      return "Calculate the forced reply and concrete payoff before rejecting a material investment.";
    default:
      return "Open a matching game and review the position before the decision, not just the result.";
  }
};

const stringValue = (value: unknown): string | null => (typeof value === "string" ? value : null);

const stringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

export const tacticalEvidenceSummary = (
  motif: string,
  evidence: Record<string, unknown> | undefined,
): string | null => {
  if (!evidence) return null;
  const proof =
    typeof evidence.proof === "object" && evidence.proof != null ? (evidence.proof as Record<string, unknown>) : null;
  const depth = proof && typeof proof.analysisDepth === "number" ? proof.analysisDepth : null;
  const suffix = depth == null ? "" : ` · DEPTH ${depth}`;
  switch (motif) {
    case "fork":
      return `Fork from ${stringValue(evidence.attacker) ?? "the tactic square"} targeting ${stringList(evidence.targets).join(" and ")}${suffix}`;
    case "pin":
      return `Pin on ${stringValue(evidence.pinned) ?? "the target"} against the king on ${stringValue(evidence.king) ?? "its line"}${suffix}`;
    case "skewer":
      return `Skewer through ${stringValue(evidence.front) ?? "the front target"} to ${stringValue(evidence.back) ?? "the rear target"}${suffix}`;
    case "discovered_attack":
      return `Line uncovered from ${stringValue(evidence.vacated) ?? "the vacated square"}${suffix}`;
    case "back_rank_threat":
      return `Forced back-rank finish on ${stringValue(evidence.threatTo) ?? "the back rank"}${suffix}`;
    case "deflection":
      return `Defender forced from ${stringValue(evidence.defenderFrom) ?? "its square"}; ${stringValue(evidence.target) ?? "the target"} falls${suffix}`;
    case "overloaded_defender":
      return `${stringValue(evidence.defender) ?? "One defender"} was carrying ${stringValue(evidence.firstTarget) ?? "two"} and ${stringValue(evidence.secondTarget) ?? "targets"}${suffix}`;
    case "zwischenzug":
      return `In-between move before the capture on ${stringValue(evidence.delayedCaptureAt) ?? "the target"}${suffix}`;
    case "sacrifice":
      return `${stringValue(evidence.sacrificedRole) ?? "Piece"} sacrifice on ${stringValue(evidence.sacrificedOn) ?? "the tactic square"}${suffix}`;
    case "mating_net":
      return `Forced mate in ${typeof evidence.mateIn === "number" ? evidence.mateIn : "?"}${suffix}`;
    case "missed_check":
    case "missed_capture":
    case "hanging_piece":
      return `Legal best move ${stringValue(proof?.bestMoveSan) ?? ""}${suffix}`.trim();
    default:
      return null;
  }
};

export const shortDate = (value: string | null): string =>
  value == null
    ? "No date recorded"
    : new Intl.DateTimeFormat("en", { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));
